import { MouseEvent, useCallback, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Container, Tree } from 'rsuite'
import { TInTableColumn } from '@/client/models/inTableColumn'
import { Button } from '@/client/components/Controls'
import UpdateTemplate from './UpdateDataList'
import { useLayout, TContextMenuItem } from '@/client/components/Layout/LayoutManager'
import EditDataList from './EditDataList'
import { InDataListIcon } from '@/client/config'
import { Capabilities, hasPermission } from '@/shared'
import project from '@client/stores/projectStore'
import { TTreeNodeItem, TreeNodeProps, TreeTopProps } from '@client/stores/tabsStore'
import { useContextMenuTree } from '@/client/utils/useContextMenu'
import { findTreeNodeById } from '@/client/utils/listContextMenuDefaults'
import InDataListModel from '@client/models/inDataList.ts'
import { toastError, toastSuccess } from '@client/utils/toastMessage.tsx'
import ModalDialog from '@client/components/ModalDialog.tsx'
import useSidebarInvalidation from '@client/utils/useSidebarInvalidation.ts'
import useListContextMenuItems from '@client/utils/useListContextMenuItems.ts'

const treeTopStyle = {
    // borderBottom: '1px solid var(--color-4)',
    // padding: '0.5em 0',
    // display: 'flex',
    // flexFlow: 'row nowrap',
    // width: '100%',
    // alignItems: 'center',
    // justifyContent: 'space-between',
}

type TSelectedContextMenu = {
    id: string
    label: string
    type: string
}

const inDataListModel = new InDataListModel()

export default function InDataLists() {
    const { t } = useTranslation()
    const { addTab, NoProjectSelected, onContextMenu, openTabToRight, removeTabCopies, removeOtherTabs } = useLayout()

    const [openEditDialog, setOpenEditDialog] = useState<boolean>(false)
    const [type, setType] = useState<string>('edit')
    const [id, setId] = useState<string>('')

    const { data, loading } = project.useDataLists()
    const hasData = data && data?.length > 0
    const hasProject = Boolean(project.currentProject.value?._id)

    const [contextMenu, setContextMenu] = useState<TSelectedContextMenu | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)

    const refetchDataLists = useCallback(() => {
        project.dataLists.value = null
    }, [])

    useSidebarInvalidation({
        // prototype: listen to multiple possible table names until you standardize broadcasts
        tables: ['inDataList', 'dataList', 'inTableColumn'],
        actions: ['updated', 'deleted'],
        refetch: refetchDataLists,
    })

    const treeNodes = useMemo(() => {
        if (data?.length === 0) return []
        const dataLists: any = { label: t('Data lists'), value: 'datalists', children: [] as any[] }

        data?.forEach((item: TInTableColumn) => {
            const child: any = {
                id: item._id,
                // A list imported without a template has neither id nor name;
                // the label read "null - undefined".
                // A list without a template is a lookup table under its name; the
                // older mixed record has no name and stays "Without template".
                label: item.outTableId
                    ? `${item.outTableId} - ${item.outTableName}`
                    : item.name
                        ? `${t('Lookup table')}: ${item.name}`
                        : t('Without template'),
                value: item.outTableId || item._id,
                children: [],
            }

            item.files &&
                item.files.forEach((file) => {
                    child.children.push({
                        id: `${item._id}-${file._id}`, // "<inTableId>-<fileId>"
                        label: `${file.fileName}::${file.sheetName}`,
                        title: file.sheetName,
                        value: `${item._id} - ${file.fileName} - ${file.sheetName}`,
                        client: file.client,
                        parent: child,
                        resourceId: item._id,
                    } as never)
                })

            dataLists.children.push(child as never)
        })

        return [dataLists]
    }, [data, loading, t])

    const handleUpload = (uploadResult: any) => {
        console.log(uploadResult)
        setTimeout(refetchDataLists, 100)
    }

    const controller = {
        getTabContent(params: any) {
            const { tab } = params
            return <EditDataList tab={tab} />
        },
    }

    function onAddDataList(event: MouseEvent<HTMLElement>): void {
        setOpenEditDialog(true)
        setType('add')
        setId('')
    }

    function newTab(node: TTreeNodeItem) {
        return {
            dataId: `data-list-in-${node.id}`,
            name: node.title || node.label,
            component: node.id,
            controller: controller,
            icon: <InDataListIcon />,
            store: {
                data: node,
                resourceId: node.id,
            },
        }
    }

    function onOpenTab(node: TTreeNodeItem) {
        addTab(newTab(node))
    }

    function TreeTop({ data }: TreeTopProps) {
        return (
            <Container className="tree-top" style={treeTopStyle}>
                {hasPermission(Capabilities.data_list_write) && (
                    <Button appearance="ghost" onClick={onAddDataList}>
                        {t('Upload data list')}
                    </Button>
                )}
            </Container>
        )
    }

    function TreeNode({ node }: TreeNodeProps) {
        // Only leaves (files) are clickable / tab-opening
        if (node.children) return node.label
        return node.label
    }

    function EmptyTree() {
        //return <div className="empty-tree-list">{loading ? t('Loading ...') : t('The list is empty.')}</div>
        return (
            <div className="empty-tree-list">
                {loading ? (
                    t('Loading ...')
                ) : (
                    <>
                        {t('The list is empty.')}
                        <br />
                        <br />
                        {t('Upload first a template to assign a data list to a template.')}
                    </>
                )}
            </div>
        )

    }

    function onSelectTreeItem(node: any): void {
        const hasChildren = (node?.children?.length || 0) > 0
        if (!hasChildren) onOpenTab(node)
    }

    const getNodeById = useCallback(
        (nodeId: string): TTreeNodeItem | null => {
            return (findTreeNodeById(treeNodes, nodeId) as TTreeNodeItem) || null
        },
        [treeNodes]
    )

    const canDelete = hasPermission(Capabilities.data_list_write)

    const extraItems = useMemo<TContextMenuItem[]>(() => {
        if (!canDelete) return []

        return [
            {
                label: t('Delete'),
                onClick: (_event, current: any) => {
                    const rawId = String(current?.id || '')
                    if (!rawId) return

                    setContextMenu({
                        id: rawId,
                        label: String(current?.label || ''),
                        type: 'delete',
                    })
                },
            },
        ]
    }, [t, canDelete])

    const { show } = useListContextMenuItems<TTreeNodeItem>({
        t,
        onContextMenu,
        addTab,
        openTabToRight,
        removeTabCopies,
        removeOtherTabs,
        getItemById: getNodeById,
        createTab: newTab,
        extraItems,
        includeSeparatorBeforeExtra: true,
    })

    const handleContextMenuCapture = useContextMenuTree(treeNodes, show)

    const deleteDialogButtons = [
        { status: 'delete', label: t('Delete') },
        { status: 'cancel', label: t('Cancel') },
    ]

    function handleDeleteDialogButtons(status: string) {
        if (!contextMenu) return true

        if (status === 'cancel') {
            setContextMenu(null)
            return true
        }

        if (status === 'delete') {
            const projectId = project.currentProject.value?._id || ''
            if (!projectId) {
                setContextMenu(null)
                return true
            }

            const rawId = String(contextMenu.id || '')
            if (!rawId) {
                setContextMenu(null)
                return true
            }

            // rawId is either "<inTableId>" (delete whole list)
            // or "<inTableId>-<fileId>" (delete single file)
            const isFileDelete = rawId.includes('-')

            setIsDeleting(true)

            inDataListModel
                .delete(projectId, rawId)
                .then(() => {
                    const msg = isFileDelete
                        ? t('Input file deleted successfully.')
                        : t('Data list deleted successfully.')
                    toastSuccess(msg)
                    setTimeout(refetchDataLists, 100)
                })
                .catch((err: any) => {
                    console.error('Failed to delete data list:', err)
                    toastError(t('Failed to delete data list.'))
                })
                .finally(() => {
                    setIsDeleting(false)
                    setContextMenu(null)
                })

            // Keep dialog open until the request finishes
            return false
        }

        setContextMenu(null)
        return true
    }

    const deleteDialog = contextMenu && (
        <ModalDialog
            id="in-data-list-delete-dialog"
            title={t('Delete {{name}}?', { name: contextMenu.label })}
            buttons={deleteDialogButtons}
            onClick={handleDeleteDialogButtons}
            size="sm"
        >
            <p>
                {t('Are you sure you want to delete this data list?')}
                <br />
                {t('This action cannot be undone.')}
            </p>
        </ModalDialog>
    )

    const updloadDialogComponent = openEditDialog
        ? ReactDOM.createPortal(
            <UpdateTemplate
                db={project?.currentProject.value?._id || ''}
                id={id}
                type={type}
                setOpenDialog={setOpenEditDialog}
                onUpload={handleUpload}
            />,
            document.body
        )
        : null

    return !hasProject ? (
        NoProjectSelected()
    ) : (
        <div className="sidebar-content sidebar-content-in-data">
            <div className="sidebar-content-body"
                onContextMenuCapture={handleContextMenuCapture}
            >
                <TreeTop data={data} />

                {hasData ? (
                    <Tree
                        className="sidebar-content-tree"
                        data={treeNodes}
                        defaultExpandItemValues={['datalists']}
                        renderTreeNode={(node) => <TreeNode node={node} />}
                        onSelect={onSelectTreeItem}
                    />
                ) : (
                    <EmptyTree />
                )}
            </div>

            {updloadDialogComponent}
            {deleteDialog}
        </div>
    )
}
